import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import MassEmailComposer from '../components/email/MassEmailComposer';
import { 
  Plus, 
  Mail, 
  Eye, 
  Send, 
  Edit2, 
  Trash2, 
  Palette,
  Type,
  Image as ImageIcon,
  Link2,
  Loader2,
  CheckCircle,
  X,
  Upload,
  FolderOpen,
  Check,
  Users,
  Search,
  Filter,
  Paperclip,
  FileText,
  Sparkles
} from 'lucide-react';

const TEMPLATES = [
  {
    id: 'elegant',
    name: 'Elegant',
    description: 'Classic serif typography with warm neutral tones. Perfect for luxury hospitality.',
    preview_bg: '#FAF9F7',
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'Dark background with modern sans-serif. Makes a strong statement.',
    preview_bg: '#1A1A1A',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean white background with subtle borders. Understated sophistication.',
    preview_bg: '#FFFFFF',
  }
];

const ACCENT_COLORS = [
  { id: '#C5A059', name: 'Warm Gold' },
  { id: '#2C3E50', name: 'Deep Navy' },
  { id: '#7B6B5D', name: 'Taupe' },
  { id: '#4A4A4A', name: 'Charcoal' },
  { id: '#8B4513', name: 'Saddle Brown' }
];

export default function EmailDesigner() {
  const { user, isAdmin } = useAuth();
  const { get, post, put, del, loading } = useApi();
  const fileInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [designs, setDesigns] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [recipientMode, setRecipientMode] = useState('single');
  const [massComposerOpen, setMassComposerOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendEmail, setSendEmail] = useState('');
  const [sendName, setSendName] = useState('');
  // Document 2 · Phase 6.3 — Build & Send handoff context (set when EmailDesigner
  // is opened from BuildAndSendReview with ?from_workflow=...&tone=...)
  const [outreachWorkflow, setOutreachWorkflow] = useState(null); // { from_workflow, tone }
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [showContactPicker, setShowContactPicker] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    preheader: '',
    template_style: 'elegant',
    header_text: '',
    body_text: '',
    image_url: '',
    image_alt: '',
    video_url: '',
    video_thumbnail_url: '',
    button_text: 'View Presentation',
    button_url: '',
    footer_text: 'DH Design | Premium Hospitality Presentations',
    accent_color: '#C5A059',
    attachments: [], // PDF attachments
  });
  
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // P1a — Style Vision deck picker
  const [stylePickerOpen, setStylePickerOpen] = useState(false);
  const [styleDecks, setStyleDecks] = useState([]);
  const [loadingDecks, setLoadingDecks] = useState(false);

  const openStylePicker = async () => {
    setStylePickerOpen(true);
    setLoadingDecks(true);
    try {
      const decks = await get('/deck-library');
      setStyleDecks(Array.isArray(decks) ? decks : []);
    } catch (err) {
      toast.error('Failed to load Vision Decks');
      setStyleDecks([]);
    } finally {
      setLoadingDecks(false);
    }
  };

  // Build the public share URL for a deck. The deck-library router returns the URL on the `deck_url` field.
  const getDeckShareUrl = (deck) => deck?.deck_url || deck?.share_url || deck?.public_link || deck?.public_url || '';

  const selectStyleDeck = (deck) => {
    const url = getDeckShareUrl(deck);
    if (!url) {
      toast.error('This deck does not have a share URL yet — open it in Vision Decks to publish.');
      return;
    }
    const isDefaultButtonText = !formData.button_text || formData.button_text.trim() === '' || formData.button_text === 'View Presentation';
    setFormData({
      ...formData,
      button_url: url,
      button_text: isDefaultButtonText && deck.title
        ? `View Style Vision: ${deck.title}`
        : formData.button_text,
    });
    setStylePickerOpen(false);
    toast.success(`Linked: ${deck.title || 'Vision Deck'}`);
  };
  const attachmentInputRef = useRef(null);

  const fetchDesigns = async () => {
    try {
      const data = await get('/email-designs');
      setDesigns(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch email designs:', error);
      return [];
    }
  };

  const fetchMediaLibrary = async () => {
    try {
      const data = await get('/media');
      setMediaLibrary(data);
    } catch (error) {
      console.error('Failed to fetch media library:', error);
    }
  };

  const fetchEmailRecipients = async () => {
    try {
      const data = await get('/email-recipients');
      setEmailRecipients(data);
    } catch (error) {
      console.error('Failed to fetch email recipients:', error);
    }
  };

  // Load draft from URL parameter
  useEffect(() => {
    const loadDraft = async () => {
      const draftId = searchParams.get('draft');
      // Document 2 · Phase 5 — handoff from Outreach Email Composer
      const fromOutreach = searchParams.get('from_outreach') === '1';
      const outreachSubject = searchParams.get('subject');
      const outreachBody = searchParams.get('body');
      const outreachTo = searchParams.get('to');
      const outreachOppId = searchParams.get('opportunity_id');

      if (draftId) {
        const designsData = await fetchDesigns();
        const draft = designsData.find(d => d.id === draftId);
        if (draft) {
          setFormData({
            name: draft.name || '',
            subject: draft.subject || '',
            preheader: draft.preheader || '',
            template_style: draft.template_style || 'elegant',
            header_text: draft.header_text || '',
            body_text: draft.body_text || '',
            image_url: draft.image_url || '',
            image_alt: draft.image_alt || '',
            video_url: draft.video_url || '',
            video_thumbnail_url: draft.video_thumbnail_url || '',
            button_text: draft.button_text || 'View Presentation',
            button_url: draft.button_url || '',
            footer_text: draft.footer_text || 'DH Design | Premium Hospitality Presentations',
            accent_color: draft.accent_color || '#C5A059',
          });
          setSelectedDesign(draft);
          setCreateDialogOpen(true);
          searchParams.delete('draft');
          setSearchParams(searchParams);
          toast.success('Email draft loaded - ready to customize!');
        }
      } else if (fromOutreach && (outreachSubject || outreachBody)) {
        // Pre-fill from AI Outreach Email Composer (Document 2 · Phase 5)
        await fetchDesigns();
        setFormData((prev) => ({
          ...prev,
          name: outreachSubject ? outreachSubject.slice(0, 60) : prev.name,
          subject: outreachSubject || prev.subject,
          body_text: outreachBody || prev.body_text,
        }));
        if (outreachTo) {
          setSendEmail(outreachTo);
        }
        // Document 2 · Phase 6.3 — capture workflow handoff so the send call
        // can mark email_a_sent_at / email_b_sent_at on the workflow doc.
        const fromWorkflow = searchParams.get('from_workflow');
        const outreachTone = searchParams.get('tone');
        if (fromWorkflow && outreachTone) {
          setOutreachWorkflow({ from_workflow: fromWorkflow, tone: outreachTone });
        }
        setCreateDialogOpen(true);
        // Clear handoff params (keep opportunity_id for downstream use if any)
        ['from_outreach', 'subject', 'body', 'to', 'tone', 'from_workflow'].forEach((k) => searchParams.delete(k));
        setSearchParams(searchParams);
        toast.success('Outreach draft loaded — review and edit before sending');
        // eslint-disable-next-line no-console
        console.log('[Outreach handoff] opportunity_id =', outreachOppId, '· workflow =', fromWorkflow);
      } else {
        fetchDesigns();
      }
    };
    
    loadDraft();
    fetchMediaLibrary();
    fetchEmailRecipients();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      preheader: '',
      template_style: 'elegant',
      header_text: '',
      body_text: '',
      image_url: '',
      image_alt: '',
      video_url: '',
      video_thumbnail_url: '',
      button_text: 'View Presentation',
      button_url: '',
      footer_text: 'DH Design | Premium Hospitality Presentations',
      accent_color: '#C5A059',
      attachments: [],
    });
    setSelectedDesign(null);
  };

  // Handle PDF/Image attachment upload
  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingAttachment(true);
    try {
      for (const file of files) {
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
          toast.error(`${file.name}: Only PDF and image files allowed`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: File must be under 10MB`);
          continue;
        }

        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload-attachment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formDataUpload
        });

        if (res.ok) {
          const data = await res.json();
          setFormData(prev => ({
            ...prev,
            attachments: [...prev.attachments, {
              id: data.id,
              name: file.name,
              size: file.size,
              type: file.type,
              url: data.url
            }]
          }));
          toast.success(`${file.name} attached`);
        } else {
          const error = await res.json();
          toast.error(error.detail || `Failed to upload ${file.name}`);
        }
      }
    } catch (err) {
      toast.error('Error uploading file');
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const removeAttachment = (id) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== id)
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (selectedDesign) {
        await put(`/email-designs/${selectedDesign.id}`, formData);
        toast.success('Email design updated');
      } else {
        await post('/email-designs', formData);
        toast.success('Email design created');
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchDesigns();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (design) => {
    setSelectedDesign(design);
    setFormData({
      name: design.name,
      subject: design.subject,
      preheader: design.preheader || '',
      template_style: design.template_style,
      header_text: design.header_text || '',
      body_text: design.body_text,
      image_url: design.image_url || '',
      image_alt: design.image_alt || '',
      video_url: design.video_url || '',
      video_thumbnail_url: design.video_thumbnail_url || '',
      button_text: design.button_text,
      button_url: design.button_url,
      footer_text: design.footer_text || '',
      accent_color: design.accent_color,
      attachments: design.attachments || [],
    });
    setCreateDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await del(`/email-designs/${id}`);
      toast.success('Email design deleted');
      fetchDesigns();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handlePreview = async (design) => {
    try {
      const data = await get(`/email-designs/${design.id}/preview`);
      setPreviewHtml(data.html);
      setSelectedDesign(design);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const openSendDialog = (design) => {
    setSelectedDesign(design);
    setSendEmail('');
    setSendName('');
    setSendDialogOpen(true);
  };

  const handleSend = async () => {
    if (!sendEmail) {
      toast.error('Please enter recipient email');
      return;
    }
    
    try {
      const payload = {
        email_design_id: selectedDesign.id,
        recipient_email: sendEmail,
        recipient_name: sendName,
        subject: selectedDesign?.subject || selectedDesign?.name || 'Your Personalized Message',
      };
      // Document 2 · Phase 6.3 — pass workflow handoff so backend can mark
      // email_a_sent_at / email_b_sent_at on the build_send_workflows doc.
      if (outreachWorkflow?.from_workflow && outreachWorkflow?.tone) {
        payload.from_workflow = outreachWorkflow.from_workflow;
        payload.tone = outreachWorkflow.tone;
      }
      await post(`/email-designs/${selectedDesign.id}/send`, payload);
      toast.success(`Email sent to ${sendEmail}`);
      setSendDialogOpen(false);
      // Clear workflow handoff after successful send so subsequent sends don't
      // double-mark the same workflow tone.
      if (outreachWorkflow) setOutreachWorkflow(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP, and GIF images are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: uploadData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, image_url: data.url }));
      toast.success('Image uploaded successfully');
      fetchMediaLibrary();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const selectFromLibrary = (media) => {
    setFormData(prev => ({ ...prev, image_url: media.url }));
    setMediaDialogOpen(false);
    toast.success('Image selected from library');
  };

  const handleDeleteMedia = async (mediaId, e) => {
    e.stopPropagation();
    try {
      await del(`/media/${mediaId}`);
      toast.success('Image deleted');
      fetchMediaLibrary();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="email-designer-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold mb-2">Premium Email Designer</h1>
            <p className="text-muted-foreground">
              Create beautiful, branded emails with one image and one call-to-action
            </p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={(open) => {
            setCreateDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="create-email-design-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Email
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-email-dialog">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">
                  {selectedDesign ? 'Edit Email Design' : 'Create Premium Email'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleCreate} className="space-y-6 mt-4">
                {/* Template Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Template Style</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, template_style: template.id })}
                        className={`p-4 rounded-sm border text-left transition-all ${
                          formData.template_style === template.id
                            ? 'border-primary ring-1 ring-primary'
                            : 'border-border hover:border-muted-foreground/50'
                        }`}
                        data-testid={`template-${template.id}`}
                      >
                        <div 
                          className="w-full h-8 rounded-sm mb-2" 
                          style={{ backgroundColor: template.preview_bg, border: '1px solid #E5E5E5' }}
                        />
                        <p className="font-medium text-sm">{template.name}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Accent Color</Label>
                  <div className="flex gap-2">
                    {ACCENT_COLORS.map((color) => (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, accent_color: color.id })}
                        className={`w-10 h-10 rounded-sm transition-all ${
                          formData.accent_color === color.id
                            ? 'ring-2 ring-offset-2 ring-primary'
                            : ''
                        }`}
                        style={{ backgroundColor: color.id }}
                        title={color.name}
                        data-testid={`color-${color.id}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium">Design Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Ritz-Carlton Deck Reveal"
                      required
                      className="rounded-sm"
                      data-testid="email-name"
                    />
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium">Email Subject *</Label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Your Premium Hospitality Presentation"
                      required
                      className="rounded-sm"
                      data-testid="email-subject"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Header Text</Label>
                    <Input
                      value={formData.header_text}
                      onChange={(e) => setFormData({ ...formData, header_text: e.target.value })}
                      placeholder="Your Exclusive Presentation Awaits"
                      className="rounded-sm"
                      data-testid="email-header"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Body Text *</Label>
                    <Textarea
                      value={formData.body_text}
                      onChange={(e) => setFormData({ ...formData, body_text: e.target.value })}
                      placeholder="We've prepared a custom presentation showcasing premium uniform solutions tailored to your property's brand identity..."
                      required
                      rows={4}
                      className="rounded-sm resize-none"
                      data-testid="email-body"
                    />
                  </div>
                </div>

                {/* Image */}
                <div className="space-y-4 p-4 bg-muted/30 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <ImageIcon className="h-4 w-4" />
                      Email Image (Optional)
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        data-testid="image-file-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="rounded-sm"
                        data-testid="upload-image-btn"
                      >
                        {uploading ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Upload className="h-3 w-3 mr-1" />
                        )}
                        Upload
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setMediaDialogOpen(true)}
                        className="rounded-sm"
                        data-testid="media-library-btn"
                      >
                        <FolderOpen className="h-3 w-3 mr-1" />
                        Library
                      </Button>
                    </div>
                  </div>
                  
                  {/* Image Preview */}
                  {formData.image_url && (
                    <div className="relative">
                      <img 
                        src={formData.image_url} 
                        alt={formData.image_alt || 'Preview'} 
                        className="w-full max-h-48 object-cover rounded-sm border"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute top-2 right-2 h-6 w-6 p-0 bg-background/80 hover:bg-background rounded-sm"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label className="text-sm">Image URL</Label>
                      <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="Or paste URL directly..."
                        className="rounded-sm"
                        data-testid="email-image-url"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label className="text-sm">Image Alt Text</Label>
                      <Input
                        value={formData.image_alt}
                        onChange={(e) => setFormData({ ...formData, image_alt: e.target.value })}
                        placeholder="Premium hospitality uniforms"
                        className="rounded-sm"
                        data-testid="email-image-alt"
                      />
                    </div>
                  </div>
                </div>

                {/* Video Section */}
                <div className="space-y-4 p-4 bg-muted/30 rounded-sm border-l-4 border-[#C9A227]">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    Video (Optional)
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Add a video thumbnail with play button. When clicked, opens the video link.
                  </p>
                  
                  {formData.video_thumbnail_url && (
                    <div className="relative">
                      <div className="relative rounded-sm overflow-hidden border">
                        <img 
                          src={formData.video_thumbnail_url} 
                          alt="Video thumbnail" 
                          className="w-full h-48 object-cover"
                        />
                        {/* Play button overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                            <svg className="w-8 h-8 text-[#1A1A1A] ml-1" viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                        onClick={() => setFormData({ ...formData, video_thumbnail_url: '', video_url: '' })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Video Link (YouTube, Vimeo, or direct URL)</Label>
                      <Input
                        value={formData.video_url}
                        onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                        className="rounded-sm"
                        data-testid="email-video-url"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Video Thumbnail URL</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.video_thumbnail_url}
                          onChange={(e) => setFormData({ ...formData, video_thumbnail_url: e.target.value })}
                          placeholder="Paste thumbnail image URL or select from media library"
                          className="rounded-sm flex-1"
                          data-testid="email-video-thumbnail"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setMediaDialogOpen(true)}
                          className="rounded-sm"
                        >
                          <FolderOpen className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Tip: For YouTube, use https://img.youtube.com/vi/VIDEO_ID/maxresdefault.jpg
                      </p>
                    </div>
                  </div>
                </div>

                {/* Button */}
                <div className="space-y-4 p-4 bg-muted/30 rounded-sm">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Link2 className="h-4 w-4" />
                    Call-to-Action Button *
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Button Text</Label>
                      <Input
                        value={formData.button_text}
                        onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                        placeholder="View Presentation"
                        required
                        className="rounded-sm"
                        data-testid="email-button-text"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Button URL *</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.button_url}
                          onChange={(e) => setFormData({ ...formData, button_url: e.target.value })}
                          placeholder="https://your-deck-link.com"
                          required
                          className="rounded-sm flex-1"
                          data-testid="email-button-url"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={openStylePicker}
                          className="rounded-sm whitespace-nowrap"
                          data-testid="present-style-vision-btn"
                          title="Pick a Vision Deck to auto-fill the URL"
                        >
                          <Sparkles className="h-4 w-4 mr-1" />
                          Present Style Vision
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Tip: Click "Present Style Vision" to insert a deck from your library, or paste any URL.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Footer Text</Label>
                  <Input
                    value={formData.footer_text}
                    onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })}
                    placeholder="DH Design | Premium Hospitality Presentations"
                    className="rounded-sm"
                    data-testid="email-footer"
                  />
                </div>

                {/* PDF Attachments */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Attachments (Optional)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Attach PDF documents or images to include with this email template
                  </p>
                  
                  {/* Attachment List */}
                  {formData.attachments?.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {formData.attachments.map(att => (
                        <div key={att.id} className="flex items-center justify-between p-2 bg-muted rounded-sm">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#C5A059]" />
                            <span className="text-sm">{att.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({(att.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(att.id)}
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <input
                    type="file"
                    ref={attachmentInputRef}
                    onChange={handleAttachmentUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    multiple
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={uploadingAttachment}
                    className="rounded-sm border-dashed"
                  >
                    {uploadingAttachment ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {uploadingAttachment ? 'Uploading...' : 'Upload PDF or Image'}
                  </Button>
                  <p className="text-xs text-muted-foreground">PDF, JPG, PNG, GIF • Max 10MB each</p>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => {
                    setCreateDialogOpen(false);
                    resetForm();
                  }} className="rounded-sm">
                    Cancel
                  </Button>
                  <Button type="submit" className="rounded-sm" disabled={loading} data-testid="save-email-design-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (selectedDesign ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Designs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {designs.length === 0 ? (
            <Card className="col-span-full rounded-sm border">
              <CardContent className="py-16 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No email designs yet</p>
                <Button onClick={() => setCreateDialogOpen(true)} className="rounded-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Email
                </Button>
              </CardContent>
            </Card>
          ) : (
            designs.map((design) => (
              <Card key={design.id} className="rounded-sm border" data-testid={`email-design-${design.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base font-medium">{design.name}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">{design.subject}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {/* Status badge — green "Sent" when the design has been
                          sent at least once, neutral "Draft" otherwise. Reads
                          design.status which the backend flips on first send. */}
                      {design.status === 'sent' ? (
                        <Badge
                          variant="outline"
                          className="rounded-sm text-xs border-emerald-600 text-emerald-700 bg-emerald-50"
                          data-testid={`design-status-${design.id}`}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Sent
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="rounded-sm text-xs text-muted-foreground"
                          data-testid={`design-status-${design.id}`}
                        >
                          Draft
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className="rounded-sm text-xs capitalize"
                        style={{ borderColor: design.accent_color, color: design.accent_color }}
                      >
                        {design.template_style}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Preview Bar */}
                  <div 
                    className="h-2 rounded-full" 
                    style={{ backgroundColor: design.accent_color }}
                  />
                  
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {design.body_text}
                  </p>
                  
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>Created {formatDateTime(design.created_at)}</div>
                    {/* Send-count chip + last-sent timestamp — only renders
                        when the design has actually been sent, so untouched
                        drafts look exactly as before. */}
                    {design.status === 'sent' && (design.send_count > 0 || design.last_sent_at) && (
                      <div
                        className="flex items-center gap-1.5 text-emerald-700"
                        data-testid={`send-count-${design.id}`}
                      >
                        <Send className="h-3 w-3" />
                        <span>
                          {(design.send_count || 1)} {(design.send_count || 1) === 1 ? 'send' : 'sends'}
                          {design.last_sent_at && (
                            <> · last {formatDateTime(design.last_sent_at)}</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handlePreview(design)}
                      className="rounded-sm flex-1"
                      data-testid={`preview-${design.id}`}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Preview
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEdit(design)}
                      className="rounded-sm"
                      data-testid={`edit-${design.id}`}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => openSendDialog(design)}
                      className="rounded-sm flex-1"
                      data-testid={`send-${design.id}`}
                    >
                      <Send className="h-3 w-3 mr-1" />
                      Send
                    </Button>
                    {isAdmin && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(design.id)}
                        className="rounded-sm text-destructive hover:text-destructive"
                        data-testid={`delete-${design.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Email Preview</DialogTitle>
            </DialogHeader>
            <div className="border rounded-sm overflow-hidden bg-gray-100">
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                className="w-full h-[500px] border-0"
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPreviewDialogOpen(false)} className="rounded-sm">
                Close
              </Button>
              <Button onClick={() => {
                setPreviewDialogOpen(false);
                openSendDialog(selectedDesign);
              }} className="rounded-sm">
                <Send className="h-4 w-4 mr-2" />
                Send This Email
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Send Dialog */}
        <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Send Email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Single vs Multiple toggle */}
              <div className="flex items-center justify-between gap-2 p-3 rounded-sm border bg-muted/20" data-testid="recipient-mode-toggle">
                <div className="text-xs">
                  <div className="font-semibold uppercase tracking-widest text-muted-foreground">Send to</div>
                  <div className="text-sm mt-0.5">{recipientMode === 'multiple' ? 'Multiple recipients (mass send)' : 'Single recipient'}</div>
                </div>
                <div className="flex gap-1">
                  <Button
                    type="button" size="sm"
                    variant={recipientMode === 'single' ? 'default' : 'outline'}
                    onClick={() => setRecipientMode('single')}
                    className="rounded-sm h-8"
                    data-testid="recipient-mode-single"
                  >
                    Single
                  </Button>
                  <Button
                    type="button" size="sm"
                    variant={recipientMode === 'multiple' ? 'default' : 'outline'}
                    onClick={() => {
                      setSendDialogOpen(false);
                      setMassComposerOpen(true);
                    }}
                    className="rounded-sm h-8"
                    data-testid="recipient-mode-multiple"
                  >
                    <Users className="w-3.5 h-3.5 mr-1" /> Multiple
                  </Button>
                </div>
              </div>

              <div className="p-3 bg-muted/30 rounded-sm">
                <p className="text-sm font-medium">{selectedDesign?.name}</p>
                <p className="text-xs text-muted-foreground">{selectedDesign?.subject}</p>
              </div>
              
              {/* Email Preview */}
              <div className="border rounded-sm overflow-hidden">
                <div className="bg-muted/30 px-3 py-2 border-b">
                  <p className="text-xs font-medium text-muted-foreground">Email Preview</p>
                </div>
                <div className="p-4 bg-white max-h-64 overflow-y-auto">
                  {selectedDesign && (
                    <div 
                      className="text-sm"
                      style={{ 
                        fontFamily: selectedDesign.font_family || 'system-ui',
                        backgroundColor: selectedDesign.background_color || '#ffffff'
                      }}
                    >
                      {selectedDesign.header_text && (
                        <h2 style={{ color: selectedDesign.primary_color || '#000' }} className="text-lg font-semibold mb-2">
                          {selectedDesign.header_text}
                        </h2>
                      )}
                      {selectedDesign.greeting && (
                        <p className="mb-2">{selectedDesign.greeting}</p>
                      )}
                      {selectedDesign.body_content && (
                        <div className="mb-3 whitespace-pre-wrap">{selectedDesign.body_content}</div>
                      )}
                      {selectedDesign.cta_text && (
                        <button 
                          style={{ 
                            backgroundColor: selectedDesign.cta_color || selectedDesign.primary_color || '#000',
                            color: '#fff'
                          }}
                          className="px-4 py-2 rounded text-sm font-medium"
                        >
                          {selectedDesign.cta_text}
                        </button>
                      )}
                      {selectedDesign.signature && (
                        <p className="mt-4 text-sm text-muted-foreground">{selectedDesign.signature}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Contact Picker */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Select from Contacts</Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowContactPicker(!showContactPicker)}
                    className="rounded-sm text-xs"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    {showContactPicker ? 'Hide' : 'Browse Contacts'}
                  </Button>
                </div>
                
                {showContactPicker && (
                  <div className="border rounded-sm p-3 space-y-3 max-h-48 overflow-y-auto">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                      <Input
                        value={recipientSearch}
                        onChange={(e) => setRecipientSearch(e.target.value)}
                        placeholder="Search contacts..."
                        className="pl-7 h-8 text-sm rounded-sm"
                      />
                    </div>
                    {emailRecipients.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">No opt-in contacts available</p>
                    ) : (
                      <div className="space-y-1">
                        {emailRecipients
                          .filter(c => 
                            `${c.first_name} ${c.last_name} ${c.email} ${c.account_name}`
                              .toLowerCase()
                              .includes(recipientSearch.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((contact) => (
                            <div
                              key={contact.id}
                              className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-sm cursor-pointer"
                              onClick={() => {
                                setSendEmail(contact.email);
                                setSendName(`${contact.first_name} ${contact.last_name}`);
                                setShowContactPicker(false);
                                setRecipientSearch('');
                              }}
                            >
                              <div>
                                <p className="text-sm font-medium">{contact.first_name} {contact.last_name}</p>
                                <p className="text-xs text-muted-foreground">{contact.account_name}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs">{contact.email}</p>
                                {contact.consent_status === 'opt_in' && (
                                  <Badge variant="outline" className="text-xs rounded-sm mt-1">Opt-in</Badge>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Recipient Email *</Label>
                <Input
                  type="email"
                  value={sendEmail}
                  onChange={(e) => setSendEmail(e.target.value)}
                  placeholder="client@hotel.com"
                  required
                  className="rounded-sm"
                  data-testid="send-recipient-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Recipient Name</Label>
                <Input
                  value={sendName}
                  onChange={(e) => setSendName(e.target.value)}
                  placeholder="John Smith"
                  className="rounded-sm"
                  data-testid="send-recipient-name"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setSendDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    handlePreview(selectedDesign);
                  }} 
                  className="rounded-sm"
                  data-testid="preview-before-send-btn"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <Button onClick={handleSend} disabled={loading || !sendEmail} className="rounded-sm" data-testid="confirm-send-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Media Library Dialog */}
        <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="media-library-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Media Library</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {mediaLibrary.length === 0 ? (
                <div className="py-12 text-center">
                  <ImageIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">No images in library yet</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMediaDialogOpen(false);
                      fileInputRef.current?.click();
                    }}
                    className="rounded-sm"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload First Image
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 max-h-[400px] overflow-y-auto p-1">
                  {mediaLibrary.map((media) => (
                    <div
                      key={media.id}
                      className={`relative group cursor-pointer rounded-sm border-2 overflow-hidden transition-all hover:border-primary ${
                        formData.image_url === media.url ? 'border-primary ring-2 ring-primary/20' : 'border-transparent'
                      }`}
                      onClick={() => selectFromLibrary(media)}
                      data-testid={`media-item-${media.id}`}
                    >
                      <img
                        src={media.url}
                        alt={media.original_name}
                        className="w-full h-24 object-cover"
                      />
                      {formData.image_url === media.url && (
                        <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteMedia(media.id, e)}
                        className="absolute bottom-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 bg-background/80 hover:bg-destructive hover:text-destructive-foreground transition-opacity rounded-sm"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-xs text-white truncate">{media.original_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-sm"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload New
              </Button>
              <Button variant="outline" onClick={() => setMediaDialogOpen(false)} className="rounded-sm">
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* P1a — Vision Deck Picker Modal */}
        <Dialog open={stylePickerOpen} onOpenChange={setStylePickerOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="style-deck-picker-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#C5A059]" />
                Choose a Vision Deck to Present
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              {loadingDecks ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading your Vision Decks...
                </div>
              ) : styleDecks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FolderOpen className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No Vision Decks in your library yet.</p>
                  <p className="text-xs mt-1">Create one in Vision Decks → New Deck, then return here.</p>
                </div>
              ) : (
                styleDecks.map((deck) => {
                  const url = getDeckShareUrl(deck);
                  const hasUrl = !!url;
                  return (
                    <div
                      key={deck.id}
                      className={`flex items-center justify-between p-3 rounded-md border transition-colors ${
                        hasUrl ? 'hover:bg-stone-50 cursor-pointer border-stone-200' : 'bg-stone-50 border-stone-200 opacity-60'
                      }`}
                      onClick={() => hasUrl && selectStyleDeck(deck)}
                      data-testid={`style-deck-row-${deck.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{deck.title || 'Untitled Deck'}</span>
                          {deck.segment && (
                            <Badge variant="outline" className="text-xs">{deck.segment}</Badge>
                          )}
                          {!hasUrl && (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                              Not published
                            </Badge>
                          )}
                        </div>
                        {deck.updated_at && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Last updated {formatDateTime(deck.updated_at)}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant={hasUrl ? 'default' : 'outline'}
                        disabled={!hasUrl}
                        onClick={(e) => { e.stopPropagation(); hasUrl && selectStyleDeck(deck); }}
                        className="rounded-sm ml-3"
                        data-testid={`select-deck-${deck.id}`}
                      >
                        {hasUrl ? 'Use this deck' : 'Publish first'}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
            <div className="flex justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setStylePickerOpen(false)} className="rounded-sm">
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Mass Email Composer — pre-filled from selected design */}
        <MassEmailComposer
          open={massComposerOpen}
          onOpenChange={setMassComposerOpen}
          initialSubject={selectedDesign?.subject || ''}
          initialBody={
            selectedDesign
              ? [
                  selectedDesign.greeting && `<p>${selectedDesign.greeting}</p>`,
                  selectedDesign.body_content && `<div>${selectedDesign.body_content.replace(/\n/g, '<br>')}</div>`,
                  selectedDesign.cta_text && `<p><a href="#" style="background:${selectedDesign.cta_color || selectedDesign.primary_color || '#000'};color:#fff;padding:8px 16px;border-radius:4px;text-decoration:none;">${selectedDesign.cta_text}</a></p>`,
                ].filter(Boolean).join('\n')
              : ''
          }
        />
      </div>
    </Layout>
  );
}
